import { Link, useLocation } from "react-router";
import {
  LayoutDashboard,
  Bot,
  MessageSquare,
  Inbox,
  Wrench,
  Zap,
  Plug,
  Cable,
  Cpu,
  Users,
  Activity,
  Radio,
  Rocket,
  Server,
  Play,
  ScrollText,
  Settings,
  SendHorizontal,
  BookOpen,
  FileCode2,
} from "lucide-react";
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { useAuth } from "@/features/auth/auth-provider";
import { ROUTES } from "@/app/routes";
import { useGetApiAgents } from "@/lib/api/generated/agents/agents";
import { useGetApiChannelConnections } from "@/lib/api/generated/channels/channels";
import { useGetApiChannelDeployments } from "@/lib/api/generated/channels/channels";
import { useGetApiLightweightAutomations } from "@/lib/api/generated/automations/automations";
import { useOperatorConsoleAccess } from "@/features/operator-console/use-operator-console-access";

interface NavItem {
  label: string;
  path: string;
  icon: typeof LayoutDashboard;
  exactMatch?: boolean;
}

function NavItems({ items }: { items: NavItem[] }) {
  const location = useLocation();

  return (
    <SidebarMenu>
      {items.map((item) => {
        const isActive = item.exactMatch
          ? location.pathname === item.path
          : location.pathname.startsWith(item.path);
        return (
          <SidebarMenuItem key={item.path}>
            <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
              <Link to={item.path}>
                <item.icon />
                <span>{item.label}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        );
      })}
    </SidebarMenu>
  );
}

function TenantNavGroup({ tenantId }: { tenantId: string }) {
  const location = useLocation();
  const { isPlatformAdmin } = useAuth();
  const queryOptions = { query: { enabled: Boolean(tenantId) } };
  const { data: agents } = useGetApiAgents(queryOptions);
  const { data: automations } = useGetApiLightweightAutomations(queryOptions);
  const { data: connections } = useGetApiChannelConnections(queryOptions);
  const { data: deployments } = useGetApiChannelDeployments(queryOptions);

  const basePath = ROUTES.tenant(tenantId);
  const hasAgents = (agents?.length ?? 0) > 0;
  const hasAutomations = (automations?.length ?? 0) > 0;
  const hasConnections = (connections?.length ?? 0) > 0;
  const hasDeployments = (deployments?.length ?? 0) > 0;
  const hasTenantActivity = hasAgents || hasAutomations || hasConnections;
  const { hasAccess: hasOperatorConsoleAccess } = useOperatorConsoleAccess();

  const isOnPath = (path: string) => location.pathname.startsWith(path);

  const coreItems: NavItem[] = [
    { label: "Dashboard", path: basePath, icon: LayoutDashboard, exactMatch: true },
    { label: "Agents", path: `${basePath}/agents`, icon: Bot },
    { label: "Skills", path: `${basePath}/skills`, icon: BookOpen },
    { label: "Conversations", path: `${basePath}/conversations`, icon: MessageSquare },
    { label: "Inbox", path: `${basePath}/automation-inbox`, icon: Inbox },
    { label: "Outbound", path: `${basePath}/outbound`, icon: SendHorizontal },
    { label: "Automations", path: `${basePath}/automations`, icon: Zap },
    { label: "Plugins", path: `${basePath}/plugins`, icon: Plug },
    { label: "AI Providers", path: `${basePath}/ai-providers`, icon: Cpu },
    { label: "Connectors", path: `${basePath}/connectors`, icon: FileCode2 },
    { label: "Users", path: `${basePath}/identity`, icon: Users },
  ];

  const conditionalItems: NavItem[] = [];

  if (hasTenantActivity || isOnPath(`${basePath}/activity`)) {
    conditionalItems.push({ label: "Activity", path: `${basePath}/activity`, icon: Activity });
  }
  if (hasOperatorConsoleAccess || isOnPath(`${basePath}/operator-console`)) {
    conditionalItems.push({ label: "Operator Console", path: `${basePath}/operator-console`, icon: Wrench });
  }
  if (isPlatformAdmin) {
    conditionalItems.push({ label: "Connector Installs", path: `${basePath}/connector-installs`, icon: Cable });
  }
  if (isPlatformAdmin) {
    conditionalItems.push({ label: "Channels", path: `${basePath}/channels`, icon: Radio });
  }
  if (isPlatformAdmin) {
    conditionalItems.push({
      label: "Admin Conversations",
      path: `${basePath}/conversations/admin`,
      icon: MessageSquare,
    });
  }
  if (isPlatformAdmin && (hasDeployments || hasConnections || isOnPath(`${basePath}/deployments`))) {
    conditionalItems.push({ label: "Deployments", path: `${basePath}/deployments`, icon: Rocket });
  }
  if (isPlatformAdmin) {
    conditionalItems.push({ label: "Runtime", path: `${basePath}/runtime`, icon: Server });
  }

  return (
    <SidebarGroup>
      <SidebarGroupLabel>Workspace</SidebarGroupLabel>
      <NavItems items={[...coreItems, ...conditionalItems]} />
    </SidebarGroup>
  );
}

const platformItems: NavItem[] = [
  { label: "Executions", path: ROUTES.executions, icon: Play },
  { label: "Audit", path: ROUTES.audit, icon: ScrollText },
  { label: "Settings", path: ROUTES.settings, icon: Settings },
];

export function SidebarNav() {
  const { tenant } = useAuth();

  return (
    <>
      {tenant && <TenantNavGroup tenantId={tenant.tenantId} />}
      <SidebarGroup>
        <SidebarGroupLabel>Platform</SidebarGroupLabel>
        <NavItems items={platformItems} />
      </SidebarGroup>
    </>
  );
}
